import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import './CreditInsightReport.css';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const toDateKey = (value) => {
  if (!value) return null;

  // 1. If it's already a YYYY-MM-DD string, return as-is
  const raw = String(value).trim();
  if (DATE_ONLY_REGEX.test(raw)) return raw;

  // 2. If it's an ISO string or similar with a 'T', split to get the date part
  // This avoids UTC-shifting a string that was meant to represent a specific local date
  if (raw.includes('T')) {
    return raw.split('T')[0];
  }

  // 3. Fallback to Date object parsing for everything else (like picker input)
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  // Always use local components to avoid shifting across the day line
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDateRange = (dateRange, reportPeriod) => {
  const startKey = toDateKey(dateRange?.startDate) || toDateKey(reportPeriod?.start);
  const endKey = toDateKey(dateRange?.endDate) || toDateKey(reportPeriod?.end);
  if (!startKey || !endKey || startKey > endKey) return null;
  return { startKey, endKey };
};

const buildLineSeries = (dailyTrend, dateRange, reportPeriod, groupBy = 'day') => {
  if (!Array.isArray(dailyTrend) || dailyTrend.length === 0) return [];

  const dateBounds = getDateRange(dateRange, reportPeriod);
  if (!dateBounds) return [];

  const start = new Date(`${dateBounds.startKey}T00:00:00.000Z`);
  const end = new Date(`${dateBounds.endKey}T00:00:00.000Z`);
  const points = [];

  const usageByDate = new Map();
  dailyTrend.forEach((day) => {
    const key = toDateKey(day.date);
    if (key) usageByDate.set(key, (usageByDate.get(key) || 0) + (Number(day.daily_used) || 0));
  });

  if (groupBy === 'year') {
    const usageByYear = new Map();
    dailyTrend.forEach((day) => {
      const key = toDateKey(day.date);
      if (key) {
        const yKey = key.slice(0, 4);
        usageByYear.set(yKey, (usageByYear.get(yKey) || 0) + (Number(day.daily_used) || 0));
      }
    });
    let cursor = start.getUTCFullYear();
    const endY = end.getUTCFullYear();
    while (cursor <= endY) {
      const ky = String(cursor);
      points.push({ dateKey: ky, shortLabel: ky, value: usageByYear.get(ky) || 0 });
      cursor++;
    }
  } else if (groupBy === 'month') {
    const usageByMonth = new Map();
    dailyTrend.forEach((day) => {
      const key = toDateKey(day.date);
      if (key) {
        const mKey = key.slice(0, 7);
        usageByMonth.set(mKey, (usageByMonth.get(mKey) || 0) + (Number(day.daily_used) || 0));
      }
    });
    let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const stop = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    while (cursor <= stop) {
      const mk = cursor.toISOString().slice(0, 7);
      points.push({
        dateKey: mk,
        shortLabel: cursor.toLocaleString('default', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        }),
        value: usageByMonth.get(mk) || 0,
      });
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
  } else {
    const daysCount = (end - start) / (1000 * 60 * 60 * 24);
    for (let cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      const key = cursor.toISOString().slice(0, 10);
      const label =
        daysCount < 10
          ? `${cursor.getUTCDate()}/${cursor.getUTCMonth() + 1}`
          : `${cursor.getUTCDate()}`;
      points.push({
        dateKey: key,
        shortLabel: label,
        value: usageByDate.get(key) || 0,
      });
    }
  }
  return points;
};

const CreditInsightReport = ({
  isSuperadmin = false,
  isPersonal = false,
  dateRange,
  groupBy = 'day',
}) => {
  const { keycloak } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, label: '', value: 0 });
  const containerRef = useRef(null);
  const componentMounted = useRef(true);

  useEffect(() => {
    componentMounted.current = true;
    return () => {
      componentMounted.current = false;
    };
  }, []);

  const fetchReport = useCallback(async () => {
    // Wait for token to be available if we're doing a personal report or superadmin report
    if (!dateRange?.startDate || !dateRange?.endDate) {
      console.warn('[CreditInsightReport] fetchReport: Missing dates', dateRange);
      setLoading(false);
      return;
    }

    // Wait for token to be available if we're doing a personal report or superadmin report
    // to avoid unnecessary 401s during initialization
    if (!keycloak?.token) return;

    try {
      setLoading(true);
      setError(null);

      let endpoint = '/credits/org/insights';
      if (isSuperadmin) endpoint = '/credits/admin/insights';
      else if (isPersonal) endpoint = '/credits/my/insights';

      const res = await axios.get(
        `${endpoint}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {
          timeout: 30000, // 30s timeout
        }
      );
      if (componentMounted.current) {
        setReport(res.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('[CreditInsightReport] Fetch error:', err);
      if (componentMounted.current) {
        setError('Failed to generate insight report');
      }
    } finally {
      if (componentMounted.current) {
        setLoading(false);
      }
    }
  }, [dateRange, isSuperadmin, isPersonal, keycloak?.token]);

  useEffect(() => {
    fetchReport();
  }, [dateRange, isSuperadmin, isPersonal, keycloak?.token]);

  if (loading && !report)
    return (
      <div className="no-data-placeholder">
        <div className="spinner"></div>
        <p>Calculating insights...</p>
        <button onClick={() => fetchReport()} className="tm-btn-text" style={{ marginTop: '12px' }}>
          Force Refresh
        </button>
      </div>
    );
  if (error)
    return (
      <div className="no-data-placeholder">
        <i className="fas fa-exclamation-triangle" style={{ color: '#ef4444' }}></i>
        <p>{error}</p>
        <button onClick={() => fetchReport()} className="tm-btn-text" style={{ marginTop: '12px' }}>
          Retry
        </button>
      </div>
    );
  if (!report) return null;

  const { summary, providerBreakdown, topUsers, dailyTrend } = report;

  const trendSeries = buildLineSeries(dailyTrend, dateRange, report.period, groupBy);

  // SVG Config
  const chartHeight = 300;
  const padding = { top: 40, right: 40, bottom: 40, left: 60 };
  const chartWidth = Math.max(containerRef.current?.offsetWidth || 800, 400);
  const drawWidth = chartWidth - padding.left - padding.right;
  const drawHeight = chartHeight - padding.top - padding.bottom;

  const maxVal = Math.max(...trendSeries.map((p) => p.value), 10);
  const getX = (i) =>
    padding.left +
    (trendSeries.length > 1 ? (i * drawWidth) / (trendSeries.length - 1) : drawWidth / 2);
  const getY = (v) => padding.top + drawHeight - (v / maxVal) * drawHeight;

  // Smooth curve path
  const buildPath = (pts, isArea = false) => {
    if (pts.length < 2) return '';
    let d = `M ${getX(0)} ${getY(pts[0].value)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const x1 = getX(i);
      const y1 = getY(pts[i].value);
      const x2 = getX(i + 1);
      const y2 = getY(pts[i + 1].value);
      const cp1x = x1 + (x2 - x1) / 2;
      const cp2x = x2 - (x2 - x1) / 2;
      d += ` C ${cp1x} ${y1}, ${cp2x} ${y2}, ${x2} ${y2}`;
    }
    if (isArea) {
      d += ` L ${getX(pts.length - 1)} ${padding.top + drawHeight} L ${getX(0)} ${padding.top + drawHeight} Z`;
    }
    return d;
  };

  const linePath = buildPath(trendSeries);
  const areaPath = buildPath(trendSeries, true);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => Math.round(r * maxVal));
  const chartAnimationKey = `${dateRange?.startDate || ''}-${dateRange?.endDate || ''}-${groupBy}-${trendSeries.length}`;

  return (
    <div className="credit-insight-report animate-fade-in">
      <div className="report-summary-grid">
        <div className="summary-card">
          <div className="summary-label">
            <i className="fas fa-coins" style={{ color: '#3b82f6' }}></i> TOTAL USED
          </div>
          <div className="summary-value">
            {summary.totalUsed.toLocaleString()}
            <span className="summary-unit">CR</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">
            <i className="fas fa-calendar-day" style={{ color: '#ef4444' }}></i> TODAY USAGE
          </div>
          <div className="summary-value">
            {summary.todayUsed?.toLocaleString() || 0}
            <span className="summary-unit">CR</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">
            <i className="fas fa-bolt" style={{ color: '#f59e0b' }}></i> OPERATIONS
          </div>
          <div className="summary-value">
            {summary.totalTransactions.toLocaleString()}
            <span className="summary-unit">OPS</span>
          </div>
        </div>
        {!isSuperadmin && (
          <div className="summary-card">
            <div className="summary-label">
              <i className="fas fa-chart-line" style={{ color: '#10b981' }}></i> AVG DAILY
            </div>
            <div className="summary-value">
              {Math.round(summary.avgDailyUsage).toLocaleString()}
              <span className="summary-unit">CR</span>
            </div>
          </div>
        )}
      </div>

      <div className="chart-section">
        <div className="chart-header">
          <div>
            <div className="chart-title">Usage Trend</div>
            <div className="chart-subtitle">Daily credit consumption for this period</div>
          </div>
        </div>

        <div className="chart-container" ref={containerRef}>
          {trendSeries.length > 0 ? (
            <>
              {tooltip.show && (
                <div className="chart-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                  <div style={{ fontWeight: 700 }}>{tooltip.label}</div>
                  <div style={{ color: '#3b82f6' }}>{tooltip.value.toLocaleString()} Credits</div>
                </div>
              )}
              <svg
                key={chartAnimationKey}
                className="chart-svg"
                width="100%"
                height={chartHeight}
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              >
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>

                <g className="grid-lines">
                  {[0, 0.25, 0.5, 0.75, 1].map((r) => (
                    <line
                      key={r}
                      x1={padding.left}
                      y1={padding.top + r * drawHeight}
                      x2={chartWidth - padding.right}
                      y2={padding.top + r * drawHeight}
                    />
                  ))}
                </g>

                {areaPath && <path d={areaPath} className="area-path area-path-animate" />}
                {linePath && (
                  <path d={linePath} className="line-path line-path-animate" pathLength="1" />
                )}

                <g className="axis-labels">
                  {yTicks.map((val) => (
                    <text
                      key={val}
                      x={padding.left - 10}
                      y={getY(val) + 4}
                      className="axis-label y-axis-label"
                    >
                      {val}
                    </text>
                  ))}
                  {trendSeries.length > 0 &&
                    (() => {
                      const isLowDensity = trendSeries.some((p) => !p.shortLabel);
                      const step = isLowDensity
                        ? 1
                        : Math.max(1, Math.ceil(trendSeries.length / 10));
                      return trendSeries.map((p, i) => {
                        if (
                          !p.shortLabel ||
                          (i % step !== 0 && i !== trendSeries.length - 1 && !isLowDensity)
                        )
                          return null;
                        return (
                          <text
                            key={i}
                            x={getX(i)}
                            y={padding.top + drawHeight + 20}
                            className="axis-label x-axis-label"
                          >
                            {p.shortLabel}
                          </text>
                        );
                      });
                    })()}
                </g>

                {trendSeries.map((p, i) => (
                  <circle
                    key={i}
                    cx={getX(i)}
                    cy={getY(p.value)}
                    r="4"
                    className="chart-dot"
                    onMouseEnter={(e) => {
                      const rect = containerRef.current.getBoundingClientRect();
                      setTooltip({
                        show: true,
                        x: getX(i),
                        y: getY(p.value),
                        label: p.dateKey,
                        value: p.value,
                      });
                    }}
                    onMouseLeave={() => setTooltip((prev) => ({ ...prev, show: false }))}
                  />
                ))}
              </svg>
            </>
          ) : (
            <div className="no-data-placeholder">
              <i className="fas fa-chart-line"></i>
              <p>No enough data for this trend range</p>
            </div>
          )}
        </div>
      </div>

      {isSuperadmin && providerBreakdown.length > 0 && (
        <div style={{ padding: '0 24px 24px' }}>
          <div className="chart-title" style={{ marginBottom: '16px' }}>
            Provider Breakdown
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
            }}
          >
            {providerBreakdown.map((p) => (
              <div key={p.provider} className="summary-card" style={{ padding: '16px' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontWeight: 700, color: '#475569' }}>{p.provider}</span>
                  <span style={{ fontSize: '14px', fontWeight: 800 }}>
                    {Number(p.total_used).toLocaleString()} CR
                  </span>
                </div>
                <div
                  style={{
                    height: '6px',
                    background: '#f1f5f9',
                    borderRadius: '3px',
                    marginTop: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: p.provider === 'LUSHA' ? '#3b82f6' : '#10b981',
                      width: `${Math.min(100, (Number(p.total_used) / (summary.totalUsed || 1)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isPersonal && (
        <div style={{ padding: '0 24px 24px' }}>
          <div className="chart-title" style={{ marginBottom: '16px' }}>
            Top Consumption Channels
          </div>
          <div
            className="tm-table-wrap"
            style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
          >
            <table className="tm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Operations</th>
                  <th style={{ textAlign: 'right' }}>Total Credits</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((user, idx) => (
                  <tr key={user.user_id}>
                    <td style={{ color: '#94a3b8', fontWeight: 700 }}>{idx + 1}</td>
                    <td>
                      <div className="tm-member-cell">
                        <div
                          className="tm-avatar"
                          style={{ background: '#3b82f6', width: '28px', height: '28px' }}
                        >
                          {user.display_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span style={{ fontWeight: 600 }}>{user.display_name || 'Anonymous'}</span>
                      </div>
                    </td>
                    <td>{user.transaction_count}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                      {Number(user.total_used).toLocaleString()} CR
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditInsightReport;
